import type { Program } from "../types";

export interface ProgramConfig {
  program: Program;
  programName: string;
  programShort: string;
  primaryColor: string;
  logoPath: string;
  defaultGender: "Male" | "Female";
  publicUrl: string;
  applicationDeadline: string;
  sessionStartDate: string;
  sessionEndDate: string;
  amendmentsCloseDate: string;
  // Deadline for confirming a sponsor in the post-submission step (June 1).
  sponsorDeadline: string;
  // Round 50 — the Agreement of Financial Responsibility's cancellation
  // deadline (CANCELLATION_DEADLINE; default: session start minus 31 days, the
  // agreement's printed May 20) and the optional late-cancellation amount
  // shown in the withdrawal notice (LATE_CANCELLATION_AMOUNT; blank = omitted).
  cancellationDeadline: string;
  lateCancellationAmount: string;
  // Round 48 — adult-participant notice on Step 5 (Group A only).
  backgroundCheckUrl: string;
  backgroundCheckDeadline: string;
  draftCleanupDaysAfterDeadline: string | "NEVER";
  programInfoUrl: string;
  supportEmail: string;
  adminNotificationEmail: string;
  sessionYear: number;
}

export interface ServerConfig extends ProgramConfig {
  azureTenantId: string;
  azureClientId: string;
  azureClientSecret: string;
  sharePointSiteId: string;
  sharePointDriveId: string;
  sharePointApplicationsFolderPath: string;
  sharePointDraftsFolderPath: string;
  sharePointTrackingExcelPath: string;
  sharePointDraftsListName: string;
  // Preferred address for the Drafts list; falls back to the display name above.
  sharePointDraftsListId: string;
  // Round 17 — per-program append-only audit List (MDBS-Audit / MDGS-Audit).
  // Provisioned once by the owner; empty disables audit writes (logged clearly).
  sharePointAuditListId: string;
  senderEmail: string;
  port: number;
  sessionSecret: string;
  maxUploadMb: number;
  // Base URL used to build links in emails (magic-link resume, sponsor step).
  appBaseUrl: string;
  // Shared secret protecting admin endpoints (roster export, manual job triggers).
  adminExportKey: string;
  // Round 47 — GUIDE_LINK_ENABLED. Gates both the /guide route and the
  // in-app links to it; see guideLinkEnabledFromEnv.
  guideLinkEnabled: boolean;
  // Round 51 — DRIFT_CHECK_SCHEDULE: the weekly tracker drift email runs only
  // when this is exactly "on" (see driftCheckScheduleFromEnv). Optional so the
  // many test fixtures that predate it read as off.
  driftCheckSchedule?: boolean;
  // Round 51 — DRIFT_CHECK_EMAIL_TO: when set, the drift email goes here
  // INSTEAD of adminNotificationEmail, and the send is not recorded (a test
  // run must never suppress that week's real email). Blank = the program inbox.
  driftCheckEmailTo?: string;
}

export interface PublicConfig {
  program: Program;
  programName: string;
  programShort: string;
  primaryColor: string;
  defaultGender: "Male" | "Female";
  applicationDeadline: string;
  sessionStartDate: string;
  sessionEndDate: string;
  amendmentsCloseDate: string;
  sponsorDeadline: string;
  // Round 50 — optional so an older fixture still type-checks; the withdrawal
  // panel falls back to defaultCancellationDeadline(sessionStartDate).
  cancellationDeadline?: string;
  lateCancellationAmount?: string;
  // Round 48 — optional for the same reason as guideLinkEnabled below; the
  // notice falls back to the program defaults when a fixture omits them.
  backgroundCheckUrl?: string;
  backgroundCheckDeadline?: string;
  programInfoUrl: string;
  supportEmail: string;
  sessionYear: number;
  maxUploadMb: number;
  publicUrl: string;
  // Round 47 — show the "Application Guide" links. Optional so that a config
  // without it (an older fixture, a hand-built test value) reads as OFF, the
  // same as the setting being missing on the server.
  guideLinkEnabled?: boolean;
}

export function isLateDeadline(deadlineIso: string, today: Date = new Date()): boolean {
  const deadline = new Date(deadlineIso);
  return today.getTime() > deadline.getTime();
}

export function isAmendmentsOpen(amendmentsCloseIso: string, today: Date = new Date()): boolean {
  const close = new Date(amendmentsCloseIso);
  close.setHours(23, 59, 59, 999);
  return today.getTime() <= close.getTime();
}

export function physicalExamValidFrom(sessionStartIso: string): Date {
  const sessionStart = new Date(sessionStartIso);
  const twelveMonthsBefore = new Date(sessionStart);
  twelveMonthsBefore.setFullYear(twelveMonthsBefore.getFullYear() - 1);
  return twelveMonthsBefore;
}

// Date-only ISO strings ("YYYY-MM-DD") parse as UTC midnight. Reading them with
// local getters shifts the day backward in US time zones, so all formatting here
// pins to UTC to keep the displayed day matching the configured date.
export function formatSessionDateRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const fmt = new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", timeZone: "UTC" });
  const sameMonth =
    start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear();
  if (sameMonth) {
    const month = new Intl.DateTimeFormat("en-US", { month: "long", timeZone: "UTC" }).format(start);
    return `${month} ${start.getUTCDate()}–${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  }
  return `${fmt.format(start)} – ${fmt.format(end)}, ${end.getUTCFullYear()}`;
}

// Computes age (whole years) on `asOfIso` for someone born on `dobIso`.
// Both are date-only ISO strings ("YYYY-MM-DD") parsed as UTC midnight, so we
// compare with UTC getters to avoid local-timezone day shifts. Returns null if
// either date is missing or invalid.
export function computeAgeAsOf(dobIso: string, asOfIso: string): number | null {
  if (!dobIso || !asOfIso) return null;
  const dob = new Date(dobIso);
  const asOf = new Date(asOfIso);
  if (Number.isNaN(dob.getTime()) || Number.isNaN(asOf.getTime())) return null;
  let age = asOf.getUTCFullYear() - dob.getUTCFullYear();
  const monthDelta = asOf.getUTCMonth() - dob.getUTCMonth();
  if (monthDelta < 0 || (monthDelta === 0 && asOf.getUTCDate() < dob.getUTCDate())) {
    age -= 1;
  }
  return age;
}

export function formatIsoDate(iso: string): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(iso));
}

// Round 48 — which adult-participant notice (if any) Step 5 shows.
//   "A": 18 or older on the session start date.
//   "B": under 18 on the start date, 18 by the session end date (turns 18
//        during the session, the end date included).
//   null: everyone else, or no/invalid date of birth.
export type AdultParticipantGroup = "A" | "B" | null;

export function adultParticipantGroup(
  dobIso: string,
  sessionStartIso: string,
  sessionEndIso: string
): AdultParticipantGroup {
  const atStart = computeAgeAsOf(dobIso, sessionStartIso);
  if (atStart === null) return null;
  if (atStart >= 18) return "A";
  const atEnd = computeAgeAsOf(dobIso, sessionEndIso);
  return atEnd !== null && atEnd >= 18 ? "B" : null;
}

// Defaults for the two Round 48 settings (same value for both programs today).
export const DEFAULT_BACKGROUND_CHECK_URL = "https://opportunities.averity.com/opportunity/91868";
export const DEFAULT_BACKGROUND_CHECK_DEADLINE = "2027-06-01";
